// third-party
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { useDispatch as useAppDispatch, useSelector as useAppSelector } from 'react-redux';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';

// project import
import storage from 'redux-persist/lib/storage';
import auth from 'slices/auth';
import menu from 'slices/menu';
import snackbar from 'slices/snackbar';
import config from 'slices/config';
import { authApi } from 'services/auth';
import { promptsApi } from 'services/prompts';
import { usersApi } from 'services/users';
import { qualificationQuestionsApi } from 'services/qualification-questions';
import { videoQuestionsApi } from 'services/video-questions';
import { videoQuestionTestResultsApi } from 'services/video-question-test-results';
import { requisitionsApi } from 'services/requisitions';
import { resumeProfilesApi } from 'services/resume-profiles';
import { resumeProfileTestResultsApi } from 'services/resume-profile-test-results';
import { applicantsApi } from 'services/applicants';
import { resourcesApi } from 'services/resources';
import { rolesApi } from 'services/roles';
import { lobsApi } from 'services/lines-of-business';
import { newApplicantsApi } from 'services/new-applicants';
import { qualificationQuestionProfilesApi } from 'services/qualification-question-profiles';
import { videoQuestionProfilesApi } from 'services/video-question-profiles';
import { typingTestsApi } from 'services/typing-tests';
import { typingAssessmentTestResultsApi } from 'services/typing-assessment-test-results';
import { talentDiscoveryApi } from 'services/talent-discovery';
// ==============================|| REDUX TOOLKIT - MAIN STORE ||============================== //

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  whitelist: ['auth', 'menu']
};

const rootReducer = combineReducers({
  auth,
  menu,
  snackbar,
  config,
  [authApi.reducerPath]: lobsApi.reducer,
  [promptsApi.reducerPath]: promptsApi.reducer,
  [requisitionsApi.reducerPath]: requisitionsApi.reducer,
  [videoQuestionsApi.reducerPath]: videoQuestionsApi.reducer,
  [videoQuestionTestResultsApi.reducerPath]: videoQuestionTestResultsApi.reducer,
  [videoQuestionProfilesApi.reducerPath]: videoQuestionProfilesApi.reducer,
  [qualificationQuestionProfilesApi.reducerPath]: qualificationQuestionProfilesApi.reducer,
  [typingTestsApi.reducerPath]: typingTestsApi.reducer,
  [typingAssessmentTestResultsApi.reducerPath]: typingAssessmentTestResultsApi.reducer,
  [resumeProfilesApi.reducerPath]: resumeProfilesApi.reducer,
  [resumeProfileTestResultsApi.reducerPath]: resumeProfileTestResultsApi.reducer,
  [qualificationQuestionsApi.reducerPath]: qualificationQuestionsApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [resourcesApi.reducerPath]: resourcesApi.reducer,
  [applicantsApi.reducerPath]: applicantsApi.reducer,
  [newApplicantsApi.reducerPath]: newApplicantsApi.reducer,
  [rolesApi.reducerPath]: rolesApi.reducer,
  [lobsApi.reducerPath]: lobsApi.reducer,
  [talentDiscoveryApi.reducerPath]: talentDiscoveryApi.reducer
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    }).concat(
      authApi.middleware,
      promptsApi.middleware,
      requisitionsApi.middleware,
      videoQuestionsApi.middleware,
      videoQuestionTestResultsApi.middleware,
      videoQuestionProfilesApi.middleware,
      qualificationQuestionProfilesApi.middleware,
      typingTestsApi.middleware,
      typingAssessmentTestResultsApi.middleware,
      resumeProfilesApi.middleware,
      resumeProfileTestResultsApi.middleware,
      qualificationQuestionsApi.middleware,
      usersApi.middleware,
      resourcesApi.middleware,
      applicantsApi.middleware,
      newApplicantsApi.middleware,
      rolesApi.middleware,
      lobsApi.middleware,
      talentDiscoveryApi.middleware
    )
});

const persistor = persistStore(store);

const { dispatch } = store;

const useDispatch = () => useAppDispatch();
const useSelector = useAppSelector;

export { store, dispatch, persistor, useSelector, useDispatch };
